import React, { useEffect, useState } from 'react';
import { Quest } from '../types';

interface QuestCompleteAnimationProps {
  quest: Quest;
  rewards: {
    xp: number;
    currency: number;
    stats: { [key: string]: number };
  };
  onComplete: () => void;
}

const QuestCompleteAnimation: React.FC<QuestCompleteAnimationProps> = ({ 
  quest, 
  rewards,
  onComplete 
}) => {
  const [step, setStep] = useState(0);
  const [showRewards, setShowRewards] = useState(false);

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    // Step 1: Show checkmark (300ms)
    timers.push(setTimeout(() => setStep(1), 100));

    // Step 2: Show rewards (600ms)
    timers.push(setTimeout(() => {
      setShowRewards(true);
      setStep(2);
    }, 400));

    // Step 3: Rewards fly out (900ms)
    timers.push(setTimeout(() => setStep(3), 700));

    // Complete animation (balanced duration for readability)
    timers.push(setTimeout(() => {
      onComplete();
    }, 1000));

    return () => timers.forEach(timer => clearTimeout(timer));
  }, [onComplete]);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[9999] pointer-events-none" style={{ zIndex: 9999 }}>
      {/* Quest Complete Effect */}
      {step >= 1 && (
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Expanding Circle Effect */}
          <div 
            className="absolute rounded-full bg-green-500/20 animate-expand-fade"
            style={{
              width: '100px',
              height: '100px',
              animation: 'expand-fade 0.8s ease-out forwards'
            }}
          />
          
          {/* Checkmark */}
          <div className="relative z-10 animate-bounce-in">
            <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-full p-6 shadow-[0_0_50px_rgba(34,197,94,0.8)]">
              <svg 
                className="w-16 h-16 text-white" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={3} 
                  d="M5 13l4 4L19 7"
                  className="animate-draw-check"
                />
              </svg>
            </div>
          </div>

          {/* Quest Complete Text */}
          <div className="absolute mt-32 text-center animate-slide-up">
            <div className="text-3xl font-black font-orbitron text-green-400 drop-shadow-[0_0_10px_rgba(34,197,94,0.8)]">
              QUEST COMPLETE!
            </div>
            <div className="text-lg font-rajdhani text-gray-300 mt-2">
              {quest.title}
            </div>
          </div>
        </div>
      )}

      {/* Rewards Display & Animation */}
      {showRewards && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {/* XP Reward */}
          {rewards.xp > 0 && (
            <div 
              className={`absolute ${step >= 3 ? 'animate-fly-to-top' : 'animate-fade-in-scale'}`}
              style={{ 
                top: '45%',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              <div className="bg-blue-500/90 backdrop-blur-sm px-6 py-3 rounded-lg border-2 border-blue-300 shadow-[0_0_30px_rgba(59,130,246,0.6)]">
                <div className="text-2xl font-black font-orbitron text-white">
                  +{rewards.xp} XP
                </div>
              </div>
            </div>
          )}

          {/* Currency Reward */}
          {rewards.currency > 0 && (
            <div 
              className={`absolute ${step >= 3 ? 'animate-fly-to-top-right' : 'animate-fade-in-scale'}`}
              style={{ 
                top: '45%',
                left: '60%',
                animationDelay: '0.1s'
              }}
            >
              <div className="bg-yellow-500/90 backdrop-blur-sm px-6 py-3 rounded-lg border-2 border-yellow-300 shadow-[0_0_30px_rgba(234,179,8,0.6)]">
                <div className="text-2xl font-black font-orbitron text-white">
                  💰 +{rewards.currency}
                </div>
              </div>
            </div>
          )}

          {/* Stat Rewards */}
          {Object.entries(rewards.stats).map(([stat, value], index) => (
            value > 0 && (
              <div 
                key={stat}
                className={`absolute ${step >= 3 ? 'animate-fly-to-left' : 'animate-fade-in-scale'}`}
                style={{ 
                  top: `${35 + index * 10}%`,
                  left: '35%',
                  animationDelay: `${0.2 + index * 0.1}s`
                }}
              >
                <div className="bg-purple-500/90 backdrop-blur-sm px-5 py-2 rounded-lg border-2 border-purple-300 shadow-[0_0_30px_rgba(168,85,247,0.6)]">
                  <div className="text-lg font-bold font-orbitron text-white">
                    {stat.toUpperCase()} +{value}
                  </div>
                </div>
              </div>
            )
          ))}

          {/* Sparkle Effects */}
          {step >= 2 && (
            <div className="absolute inset-0">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-yellow-400 rounded-full animate-sparkle"
                  style={{
                    left: `${45 + Math.random() * 10}%`,
                    top: `${40 + Math.random() * 10}%`,
                    animationDelay: `${Math.random() * 0.5}s`,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestCompleteAnimation;




